export interface CalendarEvent {
  id: string;
  aplicacaoPlanoAtividadeId: number;
  primeiraDataHora: string;
  title: string;
  time: string;
  aplicacao: string;
  equipamento: string;
  areaNome: string;
  executor: Array<{
    nome: string;
    avatar: string;
  }>;
  horasPrevistas: string;
  tempoTotal: number; // minutos, para soma das horas previstas do dia
  realizada: boolean;
  atrasada: boolean; // controla exibição do ícone de exclamação no card (apenas pendentes)
  qtdAtividades: number;
  tarefasIds: number[]; // Array de IDs das tarefas para reagendamento
}

export type EventsByDate = Record<string, CalendarEvent[]>;

/**
 * Interface para o executor retornado pela API
 */
export interface AgendaTaskExecutor {
  executor: number;
  nome: string;
  fotoPath: string | null;
  tempo: number;
}

/**
 * Interface para a tarefa retornada pela API
 */
export interface AgendaTask {
  primeiraDataHora: string;
  siteNome: string;
  planoAtividade: number;
  planoAtividadeNome: string | null;
  aplicacaoPlanoAtividade: number;
  grupoAreaNome: string | null;
  subGrupoAreaNome: string | null;
  areaNome: string | null;
  equipNome: string | null;
  realizada: boolean;
  qtdAtividades: number;
  tempoTotal: number;
  status: number;
  tarefas: number[]; // Array de IDs das tarefas
  executoresTotal: number;
  executoresTempoTotal: number;
  atrasada: boolean;
  executores: AgendaTaskExecutor[];
}

/**
 * Converte uma string de tempo (ex: "08:00" ou "08:00h") para minutos
 */
export const parseTime = (timeStr: string): number => {
  const [hours, minutes] = timeStr.replace('h', '').split(':').map(Number);
  return hours * 60 + minutes;
};

/**
 * Ordena eventos por horário
 */
export const sortEventsByTime = (eventArray: CalendarEvent[]): CalendarEvent[] => {
  return [...eventArray].sort((a, b) => parseTime(a.time) - parseTime(b.time));
};

/**
 * Move um evento de uma data para outra no objeto de eventos
 */
export const moveEventToDate = (events: EventsByDate, eventId: string, targetDate: string): EventsByDate => {
  let sourceDate: string | undefined;

  // Encontra a data de origem
  Object.entries(events).forEach(([date, evs]) => {
    if (evs.find(e => e.id === eventId)) {
      sourceDate = date;
    }
  });

  if (!sourceDate || sourceDate === targetDate) {
    return events;
  }

  const newEvents = { ...events };
  const movedEvent = newEvents[sourceDate].find(e => e.id === eventId);

  if (!movedEvent) {
    return events;
  }

  // Remove da data original
  newEvents[sourceDate] = newEvents[sourceDate].filter(e => e.id !== eventId);

  // Adiciona à data de destino e ordena
  const updatedTargetEvents = [...(newEvents[targetDate] || []), movedEvent].sort(
    (a, b) => parseTime(a.time) - parseTime(b.time),
  );

  newEvents[targetDate] = updatedTargetEvents;

  return newEvents;
};

/**
 * Converte minutos para formato HH:mmh
 */
export const formatMinutesToTime = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}h`;
};

/**
 * Retorna o label de horas previstas (HH:mmh) para um conjunto de eventos do dia
 */
export const getHorasPrevistasForDay = (events: CalendarEvent[]): string => {
  const totalMinutes = events.reduce((sum, event) => sum + (event.tempoTotal ?? 0), 0);
  return formatMinutesToTime(totalMinutes);
};

/**
 * Processa array de executores da API para formato do CalendarEvent
 */
const processExecutores = (executores: AgendaTaskExecutor[]): Array<{ nome: string; avatar: string }> => {
  if (!executores || executores.length === 0) {
    return [];
  }

  const baseUrl = process.env.REACT_APP_URL_IMAGES || '';
  const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;

  return executores.map(executor => {
    let fotoUrl = '';
    if (executor.fotoPath) {
      const cleanPath = executor.fotoPath.startsWith('/') ? executor.fotoPath : `/${executor.fotoPath}`;
      fotoUrl = `${cleanBaseUrl}${cleanPath}`;
      if (!fotoUrl || fotoUrl === '/') {
        fotoUrl = '';
      }
    }
    return { nome: executor.nome, avatar: fotoUrl };
  });
};

/**
 * Transforma tarefas da API em eventos do calendário
 */
export const transformAgendaTasksToEvents = (tasks: AgendaTask[]): EventsByDate => {
  const eventsByDate: EventsByDate = {};

  tasks.forEach(task => {
    // Extrair data de primeiraDataHora
    const dateObj = new Date(task.primeiraDataHora);
    const dateKey = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}-${String(
      dateObj.getDate(),
    ).padStart(2, '0')}`;

    // Extrair hora
    const hours = String(dateObj.getHours()).padStart(2, '0');
    const minutes = String(dateObj.getMinutes()).padStart(2, '0');
    const time = `${hours}:${minutes}`;

    // Processar executores
    const executores = processExecutores(task.executores || []);

    const eventId = `${task.aplicacaoPlanoAtividade}-${task.primeiraDataHora}`;

    // Criar evento
    const event: CalendarEvent = {
      id: eventId,
      aplicacaoPlanoAtividadeId: task.aplicacaoPlanoAtividade,
      primeiraDataHora: task.primeiraDataHora,
      title: task.siteNome || 'Tarefa sem título',
      time,
      aplicacao: task.planoAtividadeNome || '-',
      equipamento: task.equipNome || task.areaNome || '',
      areaNome: task.areaNome || '',
      executor: executores,
      horasPrevistas: formatMinutesToTime(task.tempoTotal || 0),
      tempoTotal: task.tempoTotal || 0,
      realizada: task.realizada || false,
      atrasada: task.atrasada ?? false,
      qtdAtividades: task.qtdAtividades || 0,
      tarefasIds: task.tarefas || [],
    };

    // Adicionar ao objeto agrupado por data
    if (!eventsByDate[dateKey]) {
      eventsByDate[dateKey] = [];
    }
    eventsByDate[dateKey].push(event);
  });

  // Ordenar eventos por horário em cada data
  Object.keys(eventsByDate).forEach(dateKey => {
    eventsByDate[dateKey] = sortEventsByTime(eventsByDate[dateKey]);
  });

  return eventsByDate;
};
